/**
 * CustomTable.js
 *
 * This is a reusable table component that serves as the foundation for displaying various types of data
 * in a tabular format. It supports features like infinite scrolling, edit/delete modes, and different
 * row types based on the data being displayed.
 *
 * Key Features:
 * - Infinite scrolling with Intersection Observer
 * - Support for edit and delete modes
 * - Dynamic row rendering based on table type
 * - Loading states for initial load and pagination
 * - Sticky header for better UX with large datasets
 *
 * Props:
 * @param {Array} dataArray - The array of data to be displayed in the table
 * @param {boolean} isLoading - Indicates if the initial data is being loaded
 * @param {boolean} isFetchingNextPage - Indicates if more data is being loaded
 * @param {boolean} editMode - Whether the table is in edit mode
 * @param {boolean} deleteMode - Whether the table is in delete mode
 * @param {Array} selectedRows - Array of selected row IDs
 * @param {Function} setselectedRows - Function to update selected rows
 * @param {Function} fetchNextPage - Function to load more data
 * @param {boolean} hasNextPage - Whether there is more data to load
 * @param {Function} refetchRows - Function to refresh the data
 * @param {Function} setMessage - Function to set status messages
 * @param {Array} tableHeaders - Array of column headers
 * @param {Function} cancelEditMode - Function to exit edit mode
 * @param {string} TableId - Identifier for the type of table (offsets, flags, aws_mapping)
 */

// CustomTable.js
import React, { useCallback, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Checkbox,
} from "@mui/material";
import OffsetTableRows from "./OffsetTableRows.js";
import FlagTableRows from "./FlagTableRows.js";
import AwsMappingTableRows from "./AwsMappingTableRows.js";

const CustomTable = ({
  dataArray,
  isLoading,
  isFetchingNextPage,
  editMode,
  deleteMode,
  selectedRows,
  setselectedRows,
  fetchNextPage,
  hasNextPage,
  refetchRows,
  setMessage,
  tableHeaders,
  cancelEditMode,
  TableId,
}) => {
  /**
   * Observer for infinite scrolling
   * Uses Intersection Observer API to detect when the last row is visible
   * and triggers loading of more data
   */
  const observer = useRef();
  const lastRowRef = useCallback(
    (node) => {
      if (isLoading || isFetchingNextPage) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage();
        }
      });
      if (node) observer.current.observe(node);
    },
    [isLoading, isFetchingNextPage, hasNextPage, fetchNextPage]
  );
  const renderTableRow = (data, index) => {
    const commonProps = {
      key: data.id,
      data,
      editMode,
      deleteMode,
      selectedRows,
      setselectedRows,
      refetchRows,
      setMessage,
      ref: index === dataArray.length - 1 ? lastRowRef : null,
      cancelEditMode,
    };

    switch (TableId) {
      case "offsets":
        return <OffsetTableRows {...commonProps} />;
      case "flags":
        return <FlagTableRows {...commonProps} />;
      case "aws_mapping":
        return <AwsMappingTableRows {...commonProps} />;
      default:
        return null; // Or a default row component if needed
    }
  };

  return (
    <TableContainer style={{ maxHeight: 600, overflow: "auto" }}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            {deleteMode && <TableCell>Select</TableCell>}
            {editMode && <TableCell>Actions</TableCell>}
            {tableHeaders.map((heading, index) => (
              <TableCell key={index}>{heading}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading && dataArray.length === 0 ? (
            <TableRow>
              <TableCell colSpan={9} align="center">
                <CircularProgress />
              </TableCell>
            </TableRow>
          ) : (
            dataArray.map((data, index) => renderTableRow(data, index))
          )}
          {isFetchingNextPage && (
            <TableRow>
              <TableCell colSpan={9} align="center">
                <CircularProgress />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CustomTable;
